// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-01-30 01:25:29 +0100 (Thu, 30 Jan 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 149683 $
//  Purpose   : domain tree table
// =============================================================================
// ---Third-party imports
import React from 'react'

import {Icon} from 'semantic-ui-react'

// ---Framework imports
import {Actions, Sel} from 'uix/registries'
import log from 'uix/logging'
import {mapItems, items, size, values, get} from 'ldash'
import {Link, TranslatedTable} from 'uix/components'
import {useSelector, useMemo} from 'uix/hooks'

// ---Local imports
import {DomainIcon} from './icon'

// ---Globals

export const DomainTable = ({
  data = {}, // {odid_or_dname: {dname, field1, field2}}
  columns = [], // [react-table-col-cfg, ...]
  menuPortal,
  allDomains = false, // show only request-subdomains or all?
  dnameFilter, // show only a subset of request-subdomains (or all)
  odidFilter, // show only a subset of request-subdomains (or all)
  compact = false,
  flex = false,
  showAncestors = false,
  showDomainName = false,
  showDomainType = true,
}) => {
  let i18n = useSelector(Sel.i18n)
  let {dname} = useSelector(Sel.request)
  let domains = useSelector(Sel.domains)

  if (!dnameFilter && odidFilter) {
    dnameFilter = odidFilter.map(odid => domains.odid2name[odid])
  }

  let subdomains = useSelector(Sel.subdomains)
  subdomains = allDomains ? values(domains.data) : subdomains

  let ancestorDomains = useSelector(Sel.ancestorDomains)
  let allowed_names = subdomains.map(d => d.name)
  if (showAncestors) {
    allowed_names = [...ancestorDomains.map(d => d.name), ...allowed_names]
  }
  let row_data = useMemo(
    () =>
      mapItems(
        domains.data,
        (dname, ddata) => {
          let external_data = data[ddata.id] || data[ddata.name] || {}
          //~ log.x('XXXXXXXXXX', dname, external_data)
          return {...ddata, ...external_data}
        },
        dname => allowed_names.includes(dname) && (!dnameFilter || dnameFilter.includes(dname))
      ),
    [dname, domains, data, dnameFilter]
  )
  //~ log.x(data)

  let buildDomain = data => {
    return (
      <span style={{paddingLeft: `${data.level}em`}}>
        <DomainIcon dname={data.name} />
        <Link lazy dname={data.name} disabled={data.isGhost}>
          {showDomainName ? data.name : data.description}
        </Link>
      </span>
    )
  }

  let default_columns = [
    {
      Header: 'Domain',
      accessor: 'description',
      Cell: ({value, original}) => buildDomain(original),
    },
  ]
  if (showDomainType) {
    default_columns = [
      ...default_columns,
      {Header: 'Type', accessor: 'type', Cell: ({value}) => i18n.getType('domain', value)},
    ]
  }
  let cols = [...default_columns, ...columns]
  return (
    <TranslatedTable
      flex={flex}
      menuPortal={menuPortal}
      compact={compact}
      data={row_data}
      columns={cols}
      className="-striped -highlight"
    />
  )
}
